import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck, CheckCircle, Activity,
  PackageSearch, AlertTriangle, ZapOff, CalendarClock,
} from 'lucide-react';
import { Card, CardContent, KpiCard, cn } from '../../ui';
import { getIcon } from '../icons';
import { useDashboardData } from '../hooks/useDashboardData';
import ScopeTabs from '../components/ScopeTabs';
import StockMatrixCell from '../components/StockMatrixCell';
import GuiaRapida from '../components/GuiaRapida';
import HelpTip from '../components/HelpTip';

/**
 * Página: Dashboard del módulo Inventario  (sub-bloque III.2)
 *
 * Replica el mockup v3 validado:
 *   - Header sobrio
 *   - Tabs de scope (Interno GRUPOLAE / Colaboradores)
 *   - 4 KPIs (KpiCard del DS v2)
 *   - Matriz "Stock disponible por empresa" (celdas coloreadas por umbral)
 *   - Alertas activas (stock bajo, ITV, fin de renting…)
 *   - Distribución por tipo (barras) + Últimos movimientos (lista)
 *
 * Datos: hook useDashboardData(scope). Mientras el backend no expone el
 * endpoint agregado /dashboard, el hook sirve datos mock (ver
 * hooks/useDashboardData.js para activar el modo real el lunes).
 *
 * Reglas estéticas del proyecto respetadas:
 *   - Contenedor px-6 py-5, sin min-h-screen, sin max-w
 *   - CardContent p-4, separación de filas mb-3, gap-3 entre tarjetas
 *   - Header directo, sin cursivas editoriales
 */

// Pills de severidad para la lista de "Últimos movimientos"
const EVENT_PILL = {
  asignacion: 'bg-blue-100 text-blue-800',
  devolucion: 'bg-emerald-100 text-emerald-800',
  reparacion_inicio: 'bg-amber-100 text-amber-800',
  reparacion_fin: 'bg-emerald-100 text-emerald-800',
  baja: 'bg-rose-100 text-rose-800',
  compra: 'bg-violet-100 text-violet-800',
  edicion: 'bg-slate-100 text-slate-600',
};

const ALERT_STYLES = {
  danger: { wrap: 'bg-rose-50 border-rose-200', Icon: ZapOff, color: 'text-rose-500' },
  warning: { wrap: 'bg-amber-50 border-amber-200', Icon: CalendarClock, color: 'text-amber-500' },
  info: { wrap: 'bg-blue-50 border-blue-200', Icon: CalendarClock, color: 'text-blue-500' },
};

function Skeleton({ className }) {
  return <div className={cn('animate-pulse bg-slate-100 rounded', className)} />;
}

export default function Dashboard() {
  const [scope, setScope] = useState('interno');
  const { data, loading, error, isMock } = useDashboardData(scope);

  return (
    <div className="px-6 py-5">
      {/* 1. HEADER */}
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
        <h1 className="text-xl font-semibold text-slate-900 leading-tight">
          Dashboard de Inventario
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Visión general del estado · KPIs y movimientos recientes
          {data && (
            <span className="ml-1">
              · <span className="font-medium text-slate-700">{data.kpis.total}</span> unidades
              {' '}en {scope === 'interno' ? 'GRUPOLAE' : 'colaboradores'}
            </span>
          )}
          {isMock && (
            <span className="ml-2 text-[11px] text-amber-600 font-medium">
              · datos de demostración (backend pendiente)
            </span>
          )}
        </p>
        </div>
        <GuiaRapida seccion="dashboard" />
      </div>

      {/* 2. TABS DE SCOPE */}
      <ScopeTabs value={scope} onChange={setScope} />

      {error && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-rose-600">
              No se pudieron cargar los datos del dashboard. Inténtalo de nuevo.
            </p>
          </CardContent>
        </Card>
      )}

      {/* 3. KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {loading || !data ? (
          [0, 1, 2, 3].map((i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))
        ) : (
          <>
            <KpiCard
              icon={<CheckCircle className="size-3.5" />}
              label="Disponibles"
              value={data.kpis.disponibles}
              subtitle="listos para asignar"
            />
            <KpiCard
              icon={<UserCheck className="size-3.5" />}
              label="Asignados"
              value={data.kpis.asignados}
              subtitle={
                data.kpis.total > 0
                  ? `${Math.round((data.kpis.asignados / data.kpis.total) * 100)}% del catálogo en uso`
                  : 'sin catálogo'
              }
            />
            <KpiCard
              icon={<Activity className="size-3.5" />}
              label="Reparación / baja"
              value={data.kpis.en_reparacion + data.kpis.en_baja}
              subtitle={`${data.kpis.en_reparacion} reparación · ${data.kpis.en_baja} baja`}
            />
            <KpiCard
              icon={<AlertTriangle className="size-3.5" />}
              label="Alertas de stock"
              value={data.kpis.alertas_stock ?? 0}
              subtitle={
                (data.kpis.alertas_stock ?? 0) === 0
                  ? 'todo por encima del umbral'
                  : `${data.kpis.alertas_criticas ?? 0} ${
                      (data.kpis.alertas_criticas ?? 0) === 1 ? 'crítica' : 'críticas'
                    } · revisar compras`
              }
            />
          </>
        )}
      </div>

      {/* 4. MATRIZ STOCK DISPONIBLE POR EMPRESA */}
      <Card className="mb-3">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <PackageSearch className="w-4 h-4 text-[hsl(var(--brand-light))]" />
              Stock disponible por empresa
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Solo tipos gestionados con stock · click en celda para ver detalle
            </p>
          </div>
          <Link
            to="/inventario/ajustes"
            className="text-xs text-[hsl(var(--brand-light))] hover:underline"
          >
            Ver todas las reglas →
          </Link>
        </div>

        <div className="overflow-x-auto">
          {loading || !data ? (
            <div className="p-4"><Skeleton className="h-32 w-full" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500">
                  <th className="px-4 py-2.5 text-left font-medium">Tipo</th>
                  {data.stock_matrix.columnas.map((c) => (
                    <th key={c.id} className="px-3 py-2.5 text-center font-medium">{c.nombre}</th>
                  ))}
                  <th className="px-4 py-2.5 text-center font-medium border-l border-slate-100">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.stock_matrix.filas.map((fila) => {
                  const TipoIcon = getIcon(fila.icon);
                  return (
                    <tr key={fila.slug} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2 text-slate-700">
                          <TipoIcon className="w-3.5 h-3.5" />
                          {fila.nombre}
                        </span>
                      </td>
                      {data.stock_matrix.columnas.map((c) => {
                        const celda = fila.celdas.find((x) => x.col_id === c.id);
                        return (
                          <td key={c.id} className="px-3 py-3 text-center">
                            {celda ? (
                              <StockMatrixCell
                                cantidad={celda.cantidad}
                                estado={celda.estado}
                                umbral={celda.umbral}
                              />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center font-semibold text-slate-900 tabular-nums border-l border-slate-100">
                        {fila.total}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Leyenda */}
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-100" />OK
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-blue-100" />Info
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-amber-100" />Bajo del umbral
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-rose-100" />Crítico
          </span>
          <span className="flex items-center gap-1.5 ml-auto text-slate-400">
            El color lo define la severidad del umbral · sin regla = sin color
          </span>
        </div>
      </Card>

      {/* 5 + 5b. ALERTAS ACTIVAS · PRÓXIMOS VENCIMIENTOS (dos columnas en lg) */}
      {/*
        Truco de altura: la fila es grid items-stretch; la columna de
        vencimientos es un <div.relative> con el Card en lg:absolute lg:inset-0.
        Al estar el Card "fuera del flujo" en lg, su contenido NO empuja la
        altura de la celda → la altura de la fila la marca "Alertas activas".
        Vencimientos rellena ese alto con scroll interno si se desborda. En
        móvil (sin lg:) ambos cards vuelven a flujo normal y se apilan.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch mb-3">

        {/* Columna 1: Alertas activas (define la altura de la fila) */}
        <Card>
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Alertas activas
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {loading || !data ? '…' : `${data.alertas.length} reglas en seguimiento`} sobre inventario {scope === 'interno' ? 'interno' : 'de colaboradores'}
              </p>
            </div>
            <Link
              to="/inventario/ajustes"
              className="text-xs text-[hsl(var(--brand-light))] hover:underline"
            >
              Gestionar reglas →
            </Link>
          </div>
          <div className="p-4 space-y-2">
            {loading || !data ? (
              <Skeleton className="h-20 w-full" />
            ) : data.alertas.length === 0 ? (
              <p className="text-sm text-slate-500">Sin alertas activas.</p>
            ) : (
              data.alertas.map((al, i) => {
                const style = ALERT_STYLES[al.severidad] || ALERT_STYLES.info;
                const AlIcon = style.Icon;
                return (
                  <div
                    key={i}
                    className={cn('flex items-start gap-3 border rounded-md p-3', style.wrap)}
                  >
                    <AlIcon className={cn('w-4 h-4 mt-0.5 shrink-0', style.color)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{al.titulo}</div>
                      {al.detalle && (
                        <div className="text-xs text-slate-600 mt-0.5">{al.detalle}</div>
                      )}
                      {al.items && al.items.length > 0 && (
                        <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                          {al.items.map((it, j) => (
                            <div key={j}>· {it}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Columna 2: Próximos vencimientos (se ajusta a la altura de Alertas) */}
        <div className="relative">
          <Card className="lg:absolute lg:inset-0 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-amber-500" />
                  Próximos vencimientos
                  <HelpTip>Fechas que se acercan (ITV, caducidades). Configura los avisos en Ajustes › Alertas.</HelpTip>
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {loading || !data
                    ? '…'
                    : `${(data.vencimientos || []).length} fecha${(data.vencimientos || []).length === 1 ? '' : 's'} a vigilar en los próximos 30 días`}
                </p>
              </div>
              <Link to="/inventario/historial" className="text-xs text-[hsl(var(--brand-light))] hover:underline shrink-0">
                Ver más →
              </Link>
            </div>

            <div className="p-4 space-y-2 overflow-y-auto flex-1 min-h-0">
              {loading || !data ? (
                <Skeleton className="h-20 w-full" />
              ) : (data.vencimientos || []).length === 0 ? (
                <p className="text-sm text-slate-500">Sin vencimientos en los próximos 30 días.</p>
              ) : (
                (data.vencimientos || []).slice(0, 12).map((v, i) => {
                  const TipoIcon = getIcon(v.tipo_icon);
                  const style = ALERT_STYLES[v.severidad] || ALERT_STYLES.info;
                  return (
                    <Link
                      key={i}
                      to={`/inventario/${v.tipo_slug}/${v.asset_id}`}
                      className={cn('flex items-start gap-3 border rounded-md p-3 hover:opacity-90 transition-opacity', style.wrap)}
                    >
                      <TipoIcon className={cn('w-4 h-4 mt-0.5 shrink-0', style.color)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {v.tipo_nombre} · {v.referencia}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {v.campo_label}: {v.fecha_label}
                          {' · '}
                          {v.dias_restantes <= 0 ? 'hoy' : `en ${v.dias_restantes} día${v.dias_restantes === 1 ? '' : 's'}`}
                          {v.usuario && <> · {v.usuario}</>}
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          </Card>
        </div>

      </div>

      {/* 6. DISTRIBUCIÓN + ÚLTIMOS MOVIMIENTOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Distribución por tipo */}
        <Card>
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">Distribución por tipo</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Unidades en catálogo, ordenadas por volumen
            </p>
          </div>
          <div className="p-4 space-y-3">
            {loading || !data ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              (() => {
                const max = Math.max(...data.distribucion.map((d) => d.total), 1);
                return data.distribucion.map((d) => {
                  const DistIcon = getIcon(d.icon);
                  return (
                    <div key={d.slug}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-2">
                          <DistIcon className="w-3.5 h-3.5" />
                          {d.nombre}
                        </span>
                        <span className="font-medium text-slate-700 tabular-nums">{d.total}</span>
                      </div>
                      <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-[hsl(var(--brand-light))]"
                          style={{ width: `${Math.round((d.total / max) * 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                });
              })()
            )}
          </div>
        </Card>

        {/* Últimos movimientos */}
        <Card>
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Últimos movimientos</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Últimos eventos registrados en {scope === 'interno' ? 'interno' : 'colaboradores'}
              </p>
            </div>
            <Link
              to="/inventario/historial"
              className="text-xs text-[hsl(var(--brand-light))] hover:underline"
            >
              Ver todo el historial →
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {loading || !data ? (
              <div className="p-4"><Skeleton className="h-32 w-full" /></div>
            ) : (
              data.movimientos.map((m, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-xs">
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded text-[11px] font-medium shrink-0',
                      EVENT_PILL[m.event_type] || 'bg-slate-100 text-slate-600'
                    )}
                  >
                    {m.event_type}
                  </span>
                  <span className="flex-1 truncate">
                    {m.actor && <span className="font-medium">{m.actor} </span>}
                    {m.texto}
                  </span>
                  <span className="text-slate-400 shrink-0">{m.fecha_label}</span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
